import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  resetPassword,
  ResetPasswordSchema,
  ResetPasswordSchemaType,
} from "@/services/resetPassword";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { REGEXP_ONLY_DIGITS_AND_CHARS } from "input-otp";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

function ResetPassword() {
  const navigate = useNavigate();
  const { email } = useParams();

  const resetPasswordForm = useForm<ResetPasswordSchemaType>({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: {
      password: "",
      resetCode: "",
      email: "",
    },
  });

  const { mutate, isPending, isError, error } = useMutation({
    mutationFn: (data: ResetPasswordSchemaType) => resetPassword(data),
    onSuccess: () => {
      toast.success("Password reset successful");
      navigate("/sign-in");
    },
    onError: (error: any) => {
      console.log("Reset password error:", error);
      toast.error(
        error.response.data.message ||
          "Failed to reset password. Please try again"
      );
    },
  });
  const onSubmit = (values: ResetPasswordSchemaType) => {
    resetPasswordForm.setValue("email", email);
    mutate({ ...values, email });
  };

  useEffect(() => {
    if (email) {
      resetPasswordForm.setValue("email", email, { shouldValidate: true });
    }
  }, [email, resetPasswordForm]);

  return (
    <div className="flex items-center flex-1">
      <div className="w-full  md:w-2/5 border p-6 rounded-md mx-auto md:mt-20">
        <Form {...resetPasswordForm}>
          <form onSubmit={resetPasswordForm.handleSubmit(onSubmit)}>
            <div className="mb-4">
              <h1 className="mb-2 text-2xl font-bold">Reset password</h1>
            </div>
            <div className="grid grid-cols-1 gap-6">
              {/* <FormField
                control={resetPasswordForm.control}
                name="resetCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reset code</FormLabel>
                    <FormControl>
                      <Input placeholder="Reset code" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}
              <FormField
                control={resetPasswordForm.control}
                name="resetCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reset code</FormLabel>
                    <FormControl>
                      <InputOTP
                        maxLength={6}
                        pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
                        {...field}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup>
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup>
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </FormControl>
                    <FormDescription>
                      Please enter the one-time reset code sent to your email.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={resetPasswordForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New password</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter new password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex gap-3 justify-end w-full mt-4">
              <Button type="submit" className="" disabled={isPending}>
                {isPending ? "Sending..." : "Reset Password"}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}

export default ResetPassword;
